import React, { useState, useEffect, useCallback } from "react"
import { Loader } from "common/Loader"
import { useNotifications } from "hooks/useNotification"
import { useThunkDispatch } from "hooks/useThunkDispatch"
import { useSelector } from "react-redux"
import UserService from "services/user.service"
import { fetchData, setFilters } from "appRedux/reducers/dealerReportReducer"
import { selectFilteredData } from "appRedux/selectors/reportSelector"

const service = new UserService()

// --- Helper Functions ---
const formatCurrency = (value) => {
  if (typeof value !== "number" || isNaN(value)) return "$0.00"
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

const ThresholdFilter = ({
  thresholds,
  handleFilterChange,
  onThresholdChange,
  companyName,
  setCompanyName,
}) => {
  const handleChange = (e) => {
    const { name, value } = e.target
    const numericValue = parseFloat(value)
    if (name === "transferRate") {
      onThresholdChange((prev) => ({
        ...prev,
        [name]: isNaN(numericValue) ? 0 : numericValue / 100,
      }))
    } else {
      onThresholdChange((prev) => ({
        ...prev,
        [name]: isNaN(numericValue) ? 0 : numericValue,
      }))
    }
  }

  const handleStatusChange = (e) => {
    const { value } = e.target
    onThresholdChange((prev) => ({
      ...prev,
      campaignStatus: value,
    }))
    // handleFilterChange(value)
  }

  return (
    <div className="p-4 mb-6 bg-white border border-gray-200 rounded-lg shadow-sm">
      <h3 className="flex items-center mb-3 text-lg font-semibold text-gray-800">
        Highlight Thresholds
      </h3>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-5">
        <div className="flex flex-col">
          <label
            htmlFor="cpt-threshold"
            className="mb-1 text-sm font-medium text-gray-600"
          >
            Company Name
          </label>
          <input
            type="text"
            name="name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            className="p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
            step="0.01"
          />
        </div>
        <div className="flex flex-col">
          <label
            htmlFor="cpa-threshold"
            className="mb-1 text-sm font-medium text-gray-600"
          >
            Max CPA ($)
          </label>
          <input
            type="number"
            id="cpa-threshold"
            name="cpa"
            value={thresholds.cpa}
            onChange={handleChange}
            className="p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
            step="0.01"
          />
        </div>
        <div className="flex flex-col">
          <label
            htmlFor="transferRate-threshold"
            className="mb-1 text-sm font-medium text-gray-600"
          >
            Min Transfer Rate (%)
          </label>
          <input
            type="number"
            id="transferRate-threshold"
            name="transferRate"
            value={thresholds.transferRate * 100}
            onChange={handleChange}
            className="p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
          />
        </div>
        <div className="flex flex-col">
          <label
            htmlFor="cpt-threshold"
            className="mb-1 text-sm font-medium text-gray-600"
          >
            Max CPT ($)
          </label>
          <input
            type="number"
            id="cpt-threshold"
            name="cpt"
            value={thresholds.cpt}
            onChange={handleChange}
            className="p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
            step="0.01"
          />
        </div>

        <div className="flex flex-col">
          <label className="mb-1 text-sm font-medium text-gray-600">
            Status
          </label>
          <select
            value={thresholds.campaignStatus}
            onChange={handleStatusChange}
            name="campaignStatus"
            className="p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
          >
            <option value="Active">Active</option>
            <option value="Paused">Paused</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
      </div>
    </div>
  )
}

const TableRow = ({
  row,
  isExpanded,
  onToggle,
  isOffice = false,
  onSpendChange,
  onStatusChange,
  companyId,
  thresholds,
}) => {
  const {
    company: name,
    totalAdSpent: totalSpend,
    totalApplicantCount: candidates,
    totalScreenAcceptedCount: screened,
    totalContactedCount: contacted,
    totalInterviewAcceptedCount: accepted,
    totalUnableToContactedCount: unableToContact,
    totalInterviewRejectedCount: rejected,
    totalCpa: cpa,
    totalCpt: cpt,
    totaltr: transferRate,
    campaignStatus: status,
  } = row

  // --- Highlighting Logic ---
  const highlightClass = "bg-red-100 text-red-800 font-bold"
  const cpaHighlight = cpa > thresholds.cpa && cpa > 0 ? highlightClass : ""
  const transferRateHighlight =
    transferRate < thresholds.transferRate && candidates > 0
      ? highlightClass
      : ""
  const cptHighlight = cpt > thresholds.cpt && cpt > 0 ? highlightClass : ""

  const handleInputChange = (e) => {
    const newSpend = parseFloat(e.target.value) || 0
    onSpendChange(companyId, row._id, newSpend)
  }

  const rowClasses = `
        ${
          isOffice
            ? "bg-gray-50 hover:bg-gray-100"
            : "bg-white hover:bg-gray-50"
        }
        transition-colors duration-150
    `

  const cellClasses =
    "py-3 px-4 text-sm border-r border-gray-200 transition-colors duration-150"

  const statusStyles = {
    Active: "bg-green-100 text-green-800 border-green-200",
    Paused: "bg-yellow-100 text-yellow-800 border-yellow-200",
    Inactive: "bg-gray-200 text-gray-700 border-gray-300",
  }

  return (
    <tr className={rowClasses}>
      <td
        className={`${cellClasses} font-medium text-gray-800 sticky left-0 z-10 ${
          isOffice ? "bg-gray-50" : "bg-white"
        }`}
      >
        <div className="flex items-center">
          {isOffice ? (
            <span className="w-6 mr-2"></span>
          ) : (
            <button
              onClick={onToggle}
              className="mr-2 text-red-600hover:text-red-800"
            >
              {isExpanded ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-4 h-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m19.5 8.25-7.5 7.5-7.5-7.5"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-4 h-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m8.25 4.5 7.5 7.5-7.5 7.5"
                  />
                </svg>
              )}
            </button>
          )}
          <span className={!isOffice ? "font-semibold" : "font-normal"}>
            {isOffice ? row.officeName : name}
          </span>
        </div>
      </td>
      <td className={`${cellClasses} text-gray-600`}>
        {!isOffice ? (
          <select
            value={status}
            onChange={(e) => onStatusChange(row.companyId, e.target.value)}
            className={`w-full p-2 border rounded-md text-sm font-medium ${statusStyles[status]} focus:ring-2 focus:ring-red-500 focus:border-red-500`}
            onClick={(e) => e.stopPropagation()} // Prevent row expansion when clicking dropdown
          >
            <option value="Active">Active</option>
            <option value="Paused">Paused</option>
            <option value="Inactive">Inactive</option>
          </select>
        ) : null}
      </td>
      <td className={`${cellClasses} text-gray-600 text-right`}>
        {isOffice ? (
          <input
            type="number"
            value={row.adSpent}
            onChange={handleInputChange}
            className="w-24 p-1 text-right bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
          />
        ) : (
          formatCurrency(totalSpend)
        )}
      </td>
      <td className={`${cellClasses} text-gray-600 text-right`}>
        {isOffice ? row.applicantCount : candidates?.toLocaleString()}
      </td>
      <td className={`${cellClasses} text-gray-600 text-right`}>
        {isOffice ? row.screenAcceptedCount : screened?.toLocaleString()}
      </td>
      <td className={`${cellClasses} text-gray-600 text-right`}>
        {isOffice ? row.contactedCount : contacted?.toLocaleString()}
      </td>
      <td className={`${cellClasses} text-gray-600 text-right`}>
        {isOffice ? row.interviewAcceptedCount : accepted?.toLocaleString()}
      </td>
      <td className={`${cellClasses} text-gray-600 text-right`}>
        {isOffice
          ? row.unAbleToContactCount
          : unableToContact?.toLocaleString()}
      </td>
      <td className={`${cellClasses} text-gray-600 text-right`}>
        {isOffice ? row.interviewRejectedCount : rejected?.toLocaleString()}
      </td>
      <td
        className={`${cellClasses} text-gray-600 text-right font-medium ${cpaHighlight}`}
      >
        {formatCurrency(isOffice ? row.cpa ?? 0 : cpa)}
      </td>
      <td
        className={`${cellClasses} text-gray-600 text-right font-medium ${transferRateHighlight}`}
      >
        {isOffice
          ? Number(row.tr).toFixed(2) ?? 0
          : Number(transferRate).toFixed(2)}
      </td>
      <td
        className={`${cellClasses} text-gray-600 text-right font-medium ${cptHighlight}`}
      >
        {formatCurrency(isOffice ? row.cpt ?? 0 : cpt)}
      </td>
    </tr>
  )
}

export const DelaerReports = () => {
  const data = useSelector(selectFilteredData)
  const {filters} = useSelector((state: any) => state.reportFilters) 
  // const [data, setData] = useState<any>([])
  
  const [expandedRows, setExpandedRows] = useState<any>({})
  const [loading, setLoading] = useState(false)
  const [companyName, setCompanyName] = useState("")
  const [thresholds, setThresholds] = useState({
    cpa: filters?.cpa ||  2.5,
    transferRate: filters?.transferRate ||  0.1,
    campaignStatus: filters?.campaignStatus ||  "Active",
    cpt: filters?.cpt ||  30.0,
  })

  const { successMessage, errorMessage } = useNotifications()
  const dispatch = useThunkDispatch()

  useEffect(() => {
    dispatch(setFilters(thresholds))
  }, [thresholds])

  useEffect(() => {
    dispatch(fetchData())
  }, [dispatch])

  const handleToggleRow = (company) => {
    if (company.companyId in expandedRows) {
      const { [company.companyId]: _, ...rest } = expandedRows
      setExpandedRows(rest)
    } else {
      setExpandedRows((prev) => ({
        ...prev,
        [company.companyId]: { data: company.ads },
      }))
    }
  }

  const handleFilterChange = (status) => {
    console.log(status)
    // setData((prevData) =>
    //   prevData.filter((company) => company.campaignStatus === status)
    // )
  }

  const handleStatusChange = (companyId, newStatus) => {
    service
      .updateCompanyDetails(companyId, { campaignStatus: newStatus })
      .then(() => {
        successMessage("Company updated successfully")
        // fetchData()
      })
      .catch((error) => {
        console.log(error)
        errorMessage("Failed to update company details")
      })
  }

  const handleSpendChange = (companyId, officeId, newSpend) => {
    const newData = data?.map((company) => {
      if (company.companyId === companyId) {
        const updatedOffices = expandedRows[companyId]?.data?.map((office) =>
          office._id === officeId ? { ...office, adSpent: newSpend } : office
        )

        setExpandedRows((prev) => ({
          ...prev,
          [companyId]: { data: updatedOffices },
        }))

        setTimeout(() => {
          service
            .updateDealerMediaAd(officeId, { adSpent: newSpend })
            .then((resp) => {})
            .catch((error) => {
              console.log(error)
            })
        }, 2000)

        // const newTotalSpend = updatedOffices.reduce(
        //   (acc, office) => acc + office.totalSpend,
        //   0
        // )
        // return {
        //   ...company,
        //   totalSpend: newTotalSpend,
        //   offices: updatedOffices,
        // }
      }
      return company
    })
    // setData(newData)
  }

  const headers = [
    "Company Name",
    "Status",
    "Total Spend",
    "Candidates",
    "Screened",
    "Contacted",
    "Accepted",
    "Unable to Contact",
    "Rejected",
    "CPA",
    "Transfer Rate",
    "CPT",
  ]

  const filterData = data.filter((company) =>
    company.company.toLowerCase().includes(companyName.toLowerCase())
  )

  return (
    <div className="min-h-screen">
      <h1 className="page-title">
        Dealer Stats
      </h1>
      <p className="mb-3 text-sm text-gray-500">View all dealer stats</p>

      {/* <DateFilter onFilterChange={setDateFilter} /> */}
      <ThresholdFilter
        thresholds={thresholds}
        companyName={companyName}
        setCompanyName={setCompanyName}
        handleFilterChange={handleFilterChange}
        onThresholdChange={setThresholds}
      />

      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full text-left bg-white border-collapse table-auto">
          <thead className="bg-gray-100">
            <tr>
              {headers.map((header, index) => (
                <th
                  key={header}
                  className={`py-3 px-4 text-xs font-bold text-gray-600 uppercase tracking-wider border-b-2 border-gray-300 border-r ${
                    index === 0 ? "sticky left-0 z-20 bg-gray-100" : ""
                  } ${
                    [
                      "Total Spend",
                      "Candidates",
                      "Screened",
                      "Contacted",
                      "Accepted",
                      "Unable to Contact",
                      "Rejected",
                      "CPA",
                      "CPT",
                    ].includes(header)
                      ? "text-right"
                      : ""
                  } ${header === "Start Date" ? "text-center border-r-0" : ""}`}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filterData.length > 0 ? (
              filterData.map((company) => (
                <React.Fragment key={company.companyId}>
                  <TableRow
                    row={company}
                    onToggle={() => handleToggleRow(company)}
                    onStatusChange={handleStatusChange}
                    thresholds={thresholds}
                    isExpanded={!!expandedRows[company.companyId]}
                    onSpendChange={undefined}
                    companyId={company.companyId}
                  />
                  {company.companyId in expandedRows &&
                    expandedRows[company.companyId].data.map((office, idx) => (
                      <TableRow
                        key={idx}
                        row={office}
                        isOffice={true}
                        onSpendChange={handleSpendChange}
                        companyId={company.companyId}
                        thresholds={thresholds}
                        isExpanded={!!expandedRows[company.companyId]}
                        onToggle={undefined}
                        onStatusChange={undefined}
                      />
                    ))}
                </React.Fragment>
              ))
            ) : loading ? (
              <tr className="h-36">
                <td colSpan={headers.length}>
                  <Loader />
                </td>
              </tr>
            ) : (
              <tr>
                <td
                  colSpan={headers.length}
                  className="p-8 text-center text-gray-500"
                >
                  No data available for the selected date range.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
